import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/store";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const nav = useNavigate();
  const { cart, cartSubtotal, clearCart, user } = useStore();
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    payment_method: "stripe",
  });

  if (cart.length === 0 && !placing) {
    return (
      <div className="max-w-3xl mx-auto py-24 px-6 text-center">
        <div className="eyebrow">Your Aura</div>
        <div className="font-serif-luxe text-3xl mt-3 text-[#FAF8F5]">Your bag is empty</div>
        <button onClick={() => nav("/shop")} className="btn-gold mt-8">Shop Jewellery</button>
      </div>
    );
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const items = cart.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, slug: i.slug }));
      const payload = { ...form, items, subtotal: cartSubtotal, shipping: 0, total: cartSubtotal };
      const orderResp = await api.post("/orders", payload);
      const orderId = orderResp.data.id;

      if (form.payment_method === "stripe") {
        const co = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
        clearCart();
        window.location.href = co.data.checkout_url;
      } else {
        clearCart();
        toast.success("Order placed with an aura ✦");
        nav(`/order/${orderId}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
      setPlacing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <div className="text-center mb-12">
        <div className="eyebrow">Complete Your Journey</div>
        <h1 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3">Checkout</h1>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <div className="eyebrow mb-4">Shipping Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input testid="co-name" label="Full name" value={form.customer_name} onChange={v => set("customer_name", v)} required/>
              <Input testid="co-email" type="email" label="Email" value={form.email} onChange={v => set("email", v)} required/>
              <Input testid="co-phone" label="Phone" value={form.phone} onChange={v => set("phone", v)} required/>
              <Input testid="co-pincode" label="Pincode" value={form.pincode} onChange={v => set("pincode", v)} required/>
              <div className="sm:col-span-2"><Input testid="co-address" label="Address" value={form.address} onChange={v => set("address", v)} required/></div>
              <Input testid="co-city" label="City" value={form.city} onChange={v => set("city", v)} required/>
              <Input testid="co-state" label="State" value={form.state} onChange={v => set("state", v)} required/>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Payment</div>
            <div className="space-y-2">
              <label data-testid="pay-stripe" className={`flex items-start gap-3 p-4 border cursor-pointer transition ${form.payment_method === "stripe" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 hover:border-[#D4AF37]/50"}`}>
                <input type="radio" name="pm" checked={form.payment_method === "stripe"} onChange={() => set("payment_method", "stripe")} className="accent-[#D4AF37] mt-1"/>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm text-[#FAF8F5]"><CreditCard size={14} className="text-[#D4AF37]"/> Card / UPI via Stripe</div>
                  <div className="text-xs text-[#C2B29F] mt-1">Secure hosted checkout. Test card <span className="text-[#D4AF37]">4242 4242 4242 4242</span>, any future date, any CVC.</div>
                </div>
                <ShieldCheck className="text-[#D4AF37]" size={18}/>
              </label>
              <label data-testid="pay-cod" className={`flex items-center gap-3 p-4 border cursor-pointer ${form.payment_method === "cod" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20"}`}>
                <input type="radio" name="pm" checked={form.payment_method === "cod"} onChange={() => set("payment_method", "cod")} className="accent-[#D4AF37]"/>
                <span className="text-sm text-[#FAF8F5]">Cash on Delivery (Demo — no payment)</span>
              </label>
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#C2B29F] mt-4">
              Payments processed in USD (~${(cartSubtotal / 85).toFixed(2)}). Display prices in INR. No real card charged in test mode.
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-[#1C130E] border border-[#D4AF37]/20 p-6 gold-glow lg:sticky lg:top-32">
            <div className="eyebrow">Order Summary</div>
            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
              {cart.map(i => (
                <div key={i.product_id} className="flex gap-3">
                  <img src={i.image} alt="" className="w-14 h-14 object-cover"/>
                  <div className="flex-1 text-sm">
                    <div className="text-[#FAF8F5]">{i.name}</div>
                    <div className="text-[#C2B29F] text-xs">Qty {i.quantity}</div>
                  </div>
                  <div className="text-sm text-[#D4AF37]">{formatINR(i.price * i.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#D4AF37]/15 mt-6 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#C2B29F]"><span>Subtotal</span><span className="text-[#FAF8F5]">{formatINR(cartSubtotal)}</span></div>
              <div className="flex justify-between text-[#C2B29F]"><span>Shipping</span><span className="text-[#D4AF37]">Complimentary</span></div>
              <div className="flex justify-between text-base pt-3 border-t border-[#D4AF37]/15 mt-3"><span className="text-[#FAF8F5]">Total</span><span className="font-serif-luxe text-2xl text-[#D4AF37]">{formatINR(cartSubtotal)}</span></div>
            </div>
            <button data-testid="place-order" disabled={placing} className="btn-gold w-full mt-6">
              {placing ? "Redirecting to Stripe…" : (form.payment_method === "stripe" ? "Pay Securely" : "Place Order")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", required, testid }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.14em] uppercase text-[#C2B29F]">{label}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
      className="mt-2 w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 text-sm text-[#FAF8F5] focus:border-[#D4AF37] focus:outline-none transition"/>
  </label>
);
